/**
 * Planar Drag Toolbar Button
 *
 * Adds a "Planar" toggle button to the Inspector's gizmo toolbar.
 * Uses the same toolbar item API as the built-in gizmo buttons.
 */

import type { ServiceDefinition, IGizmoService, IShellService } from '@babylonjs/inspector';
import { GizmoServiceIdentity, ShellServiceIdentity, ToggleButton, useObservableState } from '@babylonjs/inspector';
import { ArrowMoveRegular } from '@fluentui/react-icons';
import { isPlanarModeActive, onPlanarModeChanged, setPlanarMode } from './gizmo-keyboard-service';

function PlanarToggle({ gizmoService }: { gizmoService: IGizmoService }) {
    const isActive = useObservableState(isPlanarModeActive, onPlanarModeChanged);

    return (
        <ToggleButton
            title="Planar (Y)"
            checkedIcon={ArrowMoveRegular}
            value={isActive}
            onChange={(checked) => {
                if (checked) {
                    gizmoService.gizmoMode = undefined;
                    setPlanarMode(true);
                } else {
                    setPlanarMode(false);
                }
            }}
        />
    );
}

export const PlanarDragToolbarDefinition: ServiceDefinition<[], [IShellService, IGizmoService]> = {
    friendlyName: 'Planar Drag Toolbar',
    consumes: [ShellServiceIdentity, GizmoServiceIdentity],

    factory: (shellService: IShellService, gizmoService: IGizmoService) => {
        const disposable = shellService.addToolbarItem({
            key: 'planarDrag',
            component: () => <PlanarToggle gizmoService={gizmoService} />,
            order: 10, // After built-in gizmo buttons
            horizontalLocation: 'left',
            verticalLocation: 'top',
            displayName: 'Planar Drag',
        });

        return {
            dispose: () => disposable.dispose(),
        };
    },
};
